import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subject } from "rxjs";
import { switchMap, takeUntil, filter } from 'rxjs/operators';
import {SaveVendorItemPayload, VendorTypeAhead} from "src/app/models/inventory.model";
import { ProductDetailComponent } from "src/app/modules/detail/product-detail/product-detail.component";
import { PlanyardService } from "src/app/modules/planyard/planyard.service";
import { SnackbarComponent } from '../../snackbar/snackbar.component';
import { InventoryService } from "./../../../services/inventory.service";

@Component({
  selector: "app-create-vendor-item",
  templateUrl: "./create-vendor-item.component.html",
  styleUrls: ["./create-vendor-item.component.scss"],
})
export class CreateVendorItemComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  title: string;
  spin: boolean;
  form: FormGroup = new FormGroup({
    vendor: new FormControl("", [Validators.required]),
    vendorItemName: new FormControl("", [Validators.required]),
    price: new FormControl(0.00, [Validators.required]),
    caseQty: new FormControl(1, [Validators.required]),
    localstock: new FormControl(0, [Validators.required]),
    caseRoundingSetting: new FormControl("none", [Validators.required]),
    note: new FormControl(""),
    minimumOrderQty: new FormControl(1),
    qtyPerVendorUnit: new FormControl(null),
    length:new FormControl(""),
    width:new FormControl(""),
    height:new FormControl(""),
    weight:new FormControl(""),

  });
  vendorInfo;
  filteredOptions$: Observable<VendorTypeAhead[]>;
  showWarning: boolean = false;

  constructor(
    private inventoryService: InventoryService,
    @Inject(MAT_DIALOG_DATA)
    public data: { productId?: number; vendorItem: any },
    public dialogRef: MatDialogRef<ProductDetailComponent>,
    private planyardService: PlanyardService,
    private snackBar: MatSnackBar
  ) {}
 
  ngOnInit(): void {
    if (this.data.vendorItem) {
      this.title = "Edit Vendor Item";
      const formValue = {
        vendor: {
          name: this.data.vendorItem.VendorName,
          vendorId: this.data.vendorItem.vendorId,
        },
        vendorItemName: this.data.vendorItem.VendorItemName?.trim(),
        price: this.data.vendorItem.Price,
        caseQty: this.data.vendorItem.CaseQty,
        caseRoundingSetting: this.data.vendorItem.CaseRoundingSetting,
        note: this.data.vendorItem.Notes,
        minimumOrderQty: this.data.vendorItem.MinimumOrderQty,
        qtyPerVendorUnit: this.data.vendorItem.QtyPerVendorUnit,
        length: this.data.vendorItem.VendorLength,
        width: this.data.vendorItem.VendorWidth,
        height: this.data.vendorItem.VendorHeight,
        weight: this.data.vendorItem.VendorWeight

      };
      this.fillForm(formValue);
      if (this.data.productId) {
        this.loadProduct(this.data.productId);
      }
    } else {
      this.title = "New Vendor Item";
      this.loadProduct(this.data.productId);
    }

    this.filteredOptions$ = this.form.get("vendor").valueChanges.pipe(
      filter(e => typeof e === 'string'),
      switchMap((value) => this.inventoryService.typeaheadVendor(value))
    );

    this.filteredOptions$.pipe(takeUntil(this.destroy$)).subscribe((items) => {
      if (typeof this.form.get("vendor").value === "string") {
        this.setVendor(items, this.form.get("vendor").value);
      }
    });

    const vendorItemInput = this.form.get("vendorItemName");
    const vendorInput = this.form.get('vendor');
    vendorInput.valueChanges
        .pipe(
            takeUntil(this.destroy$),
            filter(e => e.vendorId && e.name),
            switchMap((e) => this.planyardService.VendorItemExists(vendorItemInput.value, e.vendorId,this.data?.vendorItem?.ItemVendorId))
            )
        .subscribe((e) => {
         this.setErrorForVendorItemName(vendorInput, vendorItemInput, e);
        });

    vendorItemInput.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        switchMap((e) => this.planyardService.VendorItemExists(e, vendorInput.value.vendorId,this.data?.vendorItem?.ItemVendorId))
      )
      .subscribe((e) => {
        this.setErrorForVendorItemName(vendorInput, vendorItemInput, e);
      });
  }

  loadProduct(id) {
    this.planyardService.GetProductDetails(id).pipe(takeUntil(this.destroy$)).subscribe((e: any) => {
      this.vendorInfo = {
        name: e.title,
        id: e.name,
        image: e.imageUrl,
      };
    });
  }

  setVendor(items, name): void {
    const item = items.find(
      (res) => res.name.toLowerCase() === name.toLowerCase()
    );
    if (item) {
      this.form.patchValue({ vendor: item }, { emitEvent: false });
    }
  }

  setErrorForVendorItemName(vendorInput, vendorItemInput, e): void {
    if (e) {
      if (!this.data.vendorItem ||
          vendorInput.value.name !== this.data.vendorItem.VendorName ||
          vendorItemInput.value !== this.data.vendorItem.VendorItemName)
      vendorItemInput.setErrors({ unique: true });
    } else if(vendorItemInput.value){
      vendorItemInput.setErrors(null);
    }
  }

  focusOut(): void {
    if (typeof this.form.get('vendor').value === 'string') {
      this.form.get('vendor').setErrors({ incorrect: true });
    }
  }

  saveProduct() {
    const value = { ...this.form.value };
    delete value.vendor;

    const payload: SaveVendorItemPayload = {
      ...value,
    };
    payload["vendorId"] = this.form.value.vendor.vendorId;
    payload["itemId"] = this.data.productId;
    payload["caseQty"] = this.form.value.caseQty || 0;
    payload["vendorItemName"] = this.form.value.vendorItemName?.trim();
    payload['length'] = this.form.value.length || 0;
    payload['width'] = this.form.value.width || 0;
    payload['height'] = this.form.value.height || 0;
    payload['weight'] = this.form.value.weight || 0;
    if (this.data.vendorItem) {
      payload["id"] = this.data.vendorItem.ItemVendorId;
    }

    this.inventoryService.SaveVendorItem(payload).pipe(takeUntil(this.destroy$)).subscribe((e) => {
      this.snackBar.openFromComponent(SnackbarComponent,
          {
            duration: 3000,
            horizontalPosition: 'center',
            data: {
              title: 'Vendor item saved successfully',
              action: 'Dismiss',
              type: 'success',
            },
          }
      );
      this.dialogRef.close({...payload, vendorName: this.form.value.vendor.name, vendorItemId: e});
    });
  }

  fillForm(e) {
    this.form.patchValue(e);
    this.inventoryService.typeaheadVendor(e.vendor.name).pipe(takeUntil(this.destroy$)).subscribe((items) => {
      this.setVendor(items, e.vendor.name);
    });
  }

  action(e) {
    if (e === "Save") {
      this.spin = true;
      this.saveProduct();
    } else if (e === "Cancel") this.dialogRef.close();
  }
  close() {
    this.dialogRef.close();
  }

  displayWith(value: any) {
    if (value) {
      return value.name;
    }
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  restrictSpecialChar(event) {
    let k = event.charCode;
    if(k==34 || k==39) {
       return false;
     }
  }

  onChange(event) {
    var ascii = /^[ -~]+$/;
    if ( !ascii.test(event.target.value) ) {
      this.form.get('vendorItemName').setValue(event.target.value.replace(/[^\x00-\x7F]/g, ""));
    }  
  }

 
  }